import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import api from '../api';
import SignatureCanvas from 'react-signature-canvas';
import Formulario from './Formulario';
import Swal from 'sweetalert2';
import LandingCerrada from './LandingCerrada';
import './FormularioMobile.css';

const API = 'https://cna.clublostortugas.com/api/api.php';

const INITIAL_PILOTO = {
    nombres: '', cc: '', lic_fcad: '', club: '', celular: '', eps: '', rh: '',
    email: '', ciudad: '', tel_of: '', direccion: ''
};

const INITIAL_FORM_DATA = {
    evento_id: 1, valida: '', fecha: '', ciudad: '', preparado_por: '', box_no: '',
    nombre_equipo: '', categoria: '', patrocinadores: '', delegado: '',
    marca_vehiculo: '', motor: '', peso_minimo: '', cilindrad: '', tipo: '', color: '',
    datos_pilotos: [
        { ...INITIAL_PILOTO },
        { ...INITIAL_PILOTO },
        { ...INITIAL_PILOTO },
        { ...INITIAL_PILOTO }
    ],
    insc_ord: false, ext: false, rev_tec_ext: false, efectivo: false, cheque: false, recibo_no: '',
    revision_tecnica: { amortiguadores: false, anclajebateria: false, arcoseguridad: false, asiento: false, casco: false, cinturones: false, espejos: false, estacionarias: false, extintor: false, lavavidrios: false, limpiaparabrisas: false, llantas: false, luces: false, luzfrenos: false, luzposicion: false, marcaciones: false, repuesto: false, suspension: false, rines: false, pelicula: false, rh_nombre: false, maya: false },
    observaciones_tecnicas: '', no_vehiculo: '', aprobacion_comision_tecnica: '', aprobacion_comision_deportiva: '',
    firma_primer_piloto: '', firma_segundo_piloto: '', firma_tercer_piloto: '', firma_cuarto_piloto: ''
};

const FIRMA_KEYS = ['firma_primer_piloto', 'firma_segundo_piloto', 'firma_tercer_piloto', 'firma_cuarto_piloto'];

const REQUIRED_PILOT_FIELDS = [
    { key: 'nombres', label: 'Nombres y Apellidos' },
    { key: 'cc', label: 'Cédula (C.C)' },
    { key: 'lic_fcad', label: 'Lic F.C.A.D. No.' },
    { key: 'club', label: 'Club' },
    { key: 'celular', label: 'Celular' },
    { key: 'eps', label: 'E.P.S.' },
    { key: 'rh', label: 'RH (Sangre)' },
    { key: 'email', label: 'E-mail' },
    { key: 'ciudad', label: 'Ciudad' },
    { key: 'tel_of', label: 'Tel. Oficina' },
    { key: 'direccion', label: 'Dirección' }
];

function getValidationErrors(formData, pilotosActivos) {
    const errors = [];

    if (!formData.ciudad) errors.push('Ciudad del Evento');
    if (!formData.box_no) errors.push('Box No.');
    if (!formData.preparado_por) errors.push('Preparado Por');

    if (!formData.nombre_equipo) errors.push('Nombre del Equipo');
    if (!formData.categoria) errors.push('Categoría del Vehículo');
    if (!formData.no_vehiculo) errors.push('Número del Vehículo (#)');
    if (!formData.patrocinadores) errors.push('Patrocinadores');
    if (!formData.delegado) errors.push('Delegado');
    if (!formData.marca_vehiculo) errors.push('Marca Vehículo');
    if (!formData.motor) errors.push('Motor');
    if (!formData.peso_minimo) errors.push('Peso Mínimo');
    if (!formData.cilindrad) errors.push('Cilindraje');
    if (!formData.tipo) errors.push('Tipo');
    if (!formData.color) errors.push('Color');

    for (let i = 0; i < pilotosActivos; i++) {
        const pi = formData.datos_pilotos[i];
        const pNum = i + 1;

        REQUIRED_PILOT_FIELDS.forEach(field => {
            if (!pi[field.key]) errors.push(`${field.label} del Piloto ${pNum}`);
        });

        if (!formData[FIRMA_KEYS[i]]) errors.push(`Firma del Piloto ${pNum}`);
    }

    return errors;
}

function getEmailErrors(formData, pilotosActivos) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    for (let i = 0; i < pilotosActivos; i++) {
        const email = formData.datos_pilotos[i].email;
        if (email && !emailRegex.test(email)) {
            return `El correo electrónico del Piloto ${i + 1} no tiene un formato válido.`;
        }
    }
    return null;
}

const FormularioMobile = ({ editMode = false, formId = null }) => {
    const navigate = useNavigate();
    const [activeEvento, setActiveEvento] = useState(null);
    const [eventoLoading, setEventoLoading] = useState(true);
    const [formData, setFormData] = useState(INITIAL_FORM_DATA);

    const fileInputRefs = [
        React.useRef(null), React.useRef(null), React.useRef(null), React.useRef(null)
    ];

    const signaturePads = [
        React.useRef(null), React.useRef(null), React.useRef(null), React.useRef(null)
    ];

    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [pilotosActivos, setPilotosActivos] = useState(1);
    const [terminosAceptados, setTerminosAceptados] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [prePreview, setPrePreview] = useState(false);
    const [error, setError] = useState(null);
    const [validationErrors, setValidationErrors] = useState([]);
    const [showValidationModal, setShowValidationModal] = useState(false);
    const [openSection, setOpenSection] = useState('sec1');

    const resetForm = () => {
        const resetPilotos = INITIAL_FORM_DATA.datos_pilotos.map(p => ({ ...p }));
        setFormData({
            ...INITIAL_FORM_DATA,
            datos_pilotos: resetPilotos,
            evento_id: activeEvento?.id || 1,
            valida: activeEvento?.valida || '',
            fecha: (() => {
                const today = new Date();
                return today.getFullYear() + '-' +
                    String(today.getMonth() + 1).padStart(2, '0') + '-' +
                    String(today.getDate()).padStart(2, '0');
            })(),
            ciudad: activeEvento?.ciudad || ''
        });
        setPilotosActivos(1);
        setTerminosAceptados(false);
        setPrePreview(false);
        setShowPreview(false);
        setSuccess(false);
        setError(null);
        setValidationErrors([]);
        setShowValidationModal(false);
        setOpenSection('sec1');
        signaturePads.forEach(ref => {
            if (ref.current) ref.current.clear();
        });
        fileInputRefs.forEach(ref => {
            if (ref.current) ref.current.value = null;
        });
    };

    useEffect(() => {
        if (editMode) {
            setEventoLoading(false);
            return;
        }
        const fetchActiveEvento = async () => {
            try {
                const res = await axios.get(`${API}?action=get_active_event`);
                if (res.data.success) {
                    const ev = res.data.data;
                    const today = new Date();
                    const fechaHoy = today.getFullYear() + '-' +
                        String(today.getMonth() + 1).padStart(2, '0') + '-' +
                        String(today.getDate()).padStart(2, '0');
                    setActiveEvento(ev);
                    setFormData(prev => ({
                        ...prev,
                        evento_id: ev.id,
                        valida: ev.valida,
                        fecha: fechaHoy,
                        ciudad: ev.ciudad || ''
                    }));
                }
            } catch (error) {
                console.error('Error cargando evento activo', error);
            } finally {
                setEventoLoading(false);
            }
        };
        fetchActiveEvento();
    }, [editMode]);

    const [editLoading, setEditLoading] = useState(false);

    // Cargar datos del formulario en modo edición
    useEffect(() => {
        if (!editMode || !formId) return;
        const fetchForm = async () => {
            setEditLoading(true);
            try {
                const res = await api.get(`${API}?action=get_form&id=${formId}`);
                if (res.data.success) {
                    const data = res.data.data;
                    setFormData({
                        evento_id: data.evento_id || 1,
                        valida: data.valida || '',
                        fecha: data.fecha || '',
                        ciudad: data.ciudad || '',
                        preparado_por: data.preparado_por || '',
                        box_no: data.box_no || '',
                        nombre_equipo: data.nombre_equipo || '',
                        categoria: data.categoria || '',
                        patrocinadores: data.patrocinadores || '',
                        delegado: data.delegado || '',
                        marca_vehiculo: data.marca_vehiculo || '',
                        motor: data.motor || '',
                        peso_minimo: data.peso_minimo || '',
                        cilindrad: data.cilindrad || '',
                        tipo: data.tipo || '',
                        color: data.color || '',
                        datos_pilotos: Array.isArray(data.datos_pilotos)
                            ? data.datos_pilotos.map(p => ({ ...INITIAL_PILOTO, ...p }))
                            : [{ ...INITIAL_PILOTO }, { ...INITIAL_PILOTO }, { ...INITIAL_PILOTO }, { ...INITIAL_PILOTO }],
                        insc_ord: data.insc_ord == 1,
                        ext: data.ext == 1,
                        rev_tec_ext: data.rev_tec_ext == 1,
                        efectivo: data.efectivo == 1,
                        cheque: data.cheque == 1,
                        recibo_no: data.recibo_no || '',
                        revision_tecnica: typeof data.revision_tecnica === 'object' ? data.revision_tecnica : JSON.parse(data.revision_tecnica || '{}'),
                        observaciones_tecnicas: data.observaciones_tecnicas || '',
                        no_vehiculo: data.no_vehiculo || '',
                        aprobacion_comision_tecnica: data.aprobacion_comision_tecnica || '',
                        aprobacion_comision_deportiva: data.aprobacion_comision_deportiva || '',
                        firma_primer_piloto: data.firma_primer_piloto || '',
                        firma_segundo_piloto: data.firma_segundo_piloto || '',
                        firma_tercer_piloto: data.firma_tercer_piloto || '',
                        firma_cuarto_piloto: data.firma_cuarto_piloto || '',
                    });
                    // Contar cuántos pilotos tienen datos
                    const filledPilotos = (data.datos_pilotos || []).filter(p => p && (p.nombres || p.cc));
                    setPilotosActivos(Math.max(1, filledPilotos.length));
                    setActiveEvento({ id: data.evento_id, valida: data.valida, ciudad: data.ciudad });
                } else {
                    setError('No se pudo cargar la inscripción para editar.');
                }
            } catch (err) {
                console.error('Error cargando formulario para editar', err);
                setError('Error al cargar la inscripción.');
            } finally {
                setEditLoading(false);
            }
        };
        fetchForm();
    }, [editMode, formId]);

    const formDataRef = React.useRef(formData);
    formDataRef.current = formData;

    // Inicializar canvas de firma cuando cambia la sección abierta
    useEffect(() => {
        if (!openSection || !openSection.startsWith('piloto')) return;
        const idx = parseInt(openSection.replace('piloto', ''));
        const timer = setTimeout(() => {
            const pad = signaturePads[idx]?.current;
            if (pad) {
                const canvas = pad.getCanvas();
                if (canvas) {
                    const ratio = Math.max(window.devicePixelRatio || 1, 1);
                    canvas.width = canvas.offsetWidth * ratio;
                    canvas.height = canvas.offsetHeight * ratio;
                    canvas.getContext('2d').scale(ratio, ratio);

                    const currentData = formDataRef.current;
                    if (currentData[FIRMA_KEYS[idx]]) {
                        pad.fromDataURL(currentData[FIRMA_KEYS[idx]]);
                    } else {
                        pad.clear();
                    }
                }
            }
        }, 200);
        return () => clearTimeout(timer);
    }, [openSection]);

    const toggleAccordion = (section) => {
        setOpenSection(prev => prev === section ? null : section);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        const numericFields = ['box_no', 'peso_minimo', 'cilindrad', 'cc', 'celular', 'tel_of', 'no_vehiculo'];
        if (numericFields.includes(name)) {
            setFormData(prev => ({ ...prev, [name]: value.replace(/[^0-9]/g, '') }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handlePilotoChange = (index, field, value) => {
        const newPilotos = [...formData.datos_pilotos];
        const numericFields = ['cc', 'celular', 'tel_of'];
        newPilotos[index][field] = numericFields.includes(field) ? value.replace(/[^0-9]/g, '') : value;
        setFormData(prev => ({ ...prev, datos_pilotos: newPilotos }));
    };

    const handleSignatureEnd = (index) => {
        if (signaturePads[index].current) {
            const data = signaturePads[index].current.toDataURL();
            setFormData(prev => ({ ...prev, [FIRMA_KEYS[index]]: data }));
        }
    };

    const handleFileChange = (index, e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                Swal.fire({
                    title: 'Archivo muy grande',
                    text: 'El archivo de ARL no debe superar los 5MB.',
                    icon: 'warning',
                    confirmButtonColor: '#008C44',
                });
                e.target.value = null;
                return;
            }

            const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/webp'];
            if (!allowedTypes.includes(file.type)) {
                Swal.fire({
                    title: 'Tipo no permitido',
                    text: 'Solo se permiten archivos PDF, PNG, JPG o WEBP.',
                    icon: 'warning',
                    confirmButtonColor: '#008C44',
                });
                e.target.value = null;
                return;
            }

            const reader = new FileReader();
            reader.onloadend = () => {
                setFormData(prev => {
                    const newPilotos = [...prev.datos_pilotos];
                    newPilotos[index] = {
                        ...newPilotos[index],
                        arl_base64: reader.result,
                        arl_filename: file.name
                    };
                    return { ...prev, datos_pilotos: newPilotos };
                });
            };
            reader.readAsDataURL(file);
        }
    };

    const clearFile = (index) => {
        const newPilotos = [...formData.datos_pilotos];
        newPilotos[index].arl_base64 = '';
        newPilotos[index].arl_filename = '';
        setFormData(prev => ({ ...prev, datos_pilotos: newPilotos }));
        if (fileInputRefs[index].current) {
            fileInputRefs[index].current.value = null;
        }
    };

    const confirmarFormularioValido = (pilotosActivosParam) => {
        const pilotos = pilotosActivosParam ?? pilotosActivos;
        const errors = getValidationErrors(formData, pilotos);
        if (errors.length > 0) {
            setValidationErrors(errors);
            setShowValidationModal(true);
            return false;
        }

        const emailError = getEmailErrors(formData, pilotos);
        if (emailError) {
            setValidationErrors([emailError]);
            setShowValidationModal(true);
            return false;
        }

        return true;
    };

    const handlePreSubmit = (e) => {
        e.preventDefault();
        setValidationErrors([]);
        setShowValidationModal(false);
        setError(null);

        if (confirmarFormularioValido()) {
            setPrePreview(true);
            window.scrollTo(0, 0);
        }
    };

    const handleSubmit = async () => {
        if (!terminosAceptados) {
            Swal.fire({
                title: 'Términos y Condiciones',
                text: 'Debes aceptar los términos, condiciones y la exención de responsabilidad para enviar la inscripción.',
                icon: 'warning',
                confirmButtonColor: '#008C44',
                customClass: { confirmButton: 'btn btn-club rounded-pill px-4' },
                buttonsStyling: false
            });
            return;
        }

        if (!confirmarFormularioValido()) return;

        const finalFormData = {
            ...formData,
            evento_id: activeEvento.id,
            datos_pilotos: formData.datos_pilotos.slice(0, pilotosActivos),
        };
        // Limpiar firmas de pilotos eliminados
        for (let i = pilotosActivos; i < FIRMA_KEYS.length; i++) {
            finalFormData[FIRMA_KEYS[i]] = '';
        }
        if (editMode) finalFormData.id = formId;

        setLoading(true);
        setError(null);
        try {
            const res = editMode
                ? await api.post(`${API}?action=update_form`, finalFormData)
                : await axios.post(`${API}?action=submit_form`, finalFormData);

            if (res.data && res.data.success) {
                setPrePreview(false);
                setSuccess(true);
                window.scrollTo(0, 0);
            } else {
                const serverError = res.data?.error || 'Error del servidor. Intenta de nuevo más tarde.';
                throw new Error(serverError);
            }
        } catch (err) {
            const errorMsg = err.response?.data?.error || err.message;
            setError(errorMsg);
            setPrePreview(false);
            window.scrollTo(0, 0);
        } finally {
            setLoading(false);
        }
    };

    if (prePreview) {
        return (
            <div className="container-fluid min-vh-100 bg-light py-4 px-3">
                <div className="max-width-container mx-auto" style={{ maxWidth: '900px' }}>
                    <div className="card shadow-lg border-0 rounded-4 mb-4 overflow-hidden">
                        <div className="card-header bg-warning py-3 text-center border-0">
                            <h5 className="fw-bold mb-0 text-dark">
                                <i className="bi bi-eye me-2"></i> VERIFICA TUS DATOS ANTES DE ENVIAR
                            </h5>
                        </div>
                        <div className="card-body p-0">
                            <div className="alert alert-info border-0 rounded-0 mb-0 small text-center fw-bold">
                                Revisa que toda la información esté correcta. Si necesitas corregir algo, usa el botón "Regresar a Corregir".
                            </div>

                            <div className="bg-white p-2" style={{ transform: 'scale(0.95)', transformOrigin: 'top center' }}>
                                <Formulario readOnly initialData={{ ...formData, datos_pilotos: formData.datos_pilotos.slice(0, pilotosActivos) }} />
                            </div>

                            <div className="p-4 bg-white border-top shadow-sm sticky-bottom mt-3">
                                <div className="d-grid gap-3">
                                    <button
                                        type="button"
                                        className="btn btn-primary btn-lg rounded-pill py-3 fw-bold shadow-lg order-1"
                                        onClick={handleSubmit}
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <><span className="spinner-border spinner-border-sm me-2"></span> {editMode ? 'GUARDANDO...' : 'ENVIANDO...'}</>
                                        ) : (
                                            <><i className="bi bi-send-check-fill me-2"></i> {editMode ? 'GUARDAR CAMBIOS' : 'CONFIRMAR Y ENVIAR INSCRIPCIÓN OFICIAL'}</>
                                        )}
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary btn-lg rounded-pill py-3 fw-bold order-2"
                                        onClick={() => { setPrePreview(false); window.scrollTo(0, 0); }}
                                        disabled={loading}
                                    >
                                        <i className="bi bi-pencil-square me-2"></i> REGRESAR A CORREGIR DATOS
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (success) {
        return (
            <div className="mobile-success-container py-5">
                <div className="container" style={{ maxWidth: '800px' }}>
                    {!showPreview ? (
                        <div className="card shadow-lg border-0 rounded-4 p-5 text-center">
                            <div className="mb-4">
                                <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '5rem' }}></i>
                            </div>
                            {editMode ? (
                                <>
                                    <h2 className="mt-4 fw-bold">¡Inscripción Actualizada!</h2>
                                    <p className="lead text-muted mb-4">Los cambios han sido guardados correctamente.</p>
                                    <div className="d-grid gap-3">
                                        <button
                                            className="btn btn-outline-primary btn-lg rounded-pill fw-bold py-3 shadow-sm"
                                            onClick={() => setShowPreview(true)}
                                        >
                                            <i className="bi bi-file-earmark-pdf me-2"></i> Ver Inscripción Actualizada (PDF)
                                        </button>
                                        <button
                                            className="btn btn-success btn-lg rounded-pill fw-bold py-3 shadow-sm"
                                            onClick={() => navigate('/dashboard')}
                                        >
                                            <i className="bi bi-speedometer2 me-2"></i> Volver al Panel de Administración
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <h2 className="mt-4 fw-bold">¡Inscripción Exitosa!</h2>
                                    <p className="lead text-muted mb-2">Tus datos han sido registrados en el sistema del CNA.</p>
                                    <p className="text-danger fw-bold mb-4">
                                        <i className="bi bi-info-circle me-2"></i>
                                        Si presenta algún error, comuníquese con el club.
                                    </p>
                                    <div className="d-grid gap-3">
                                        <button
                                            className="btn btn-outline-primary btn-lg rounded-pill fw-bold py-3 shadow-sm"
                                            onClick={() => setShowPreview(true)}
                                        >
                                            <i className="bi bi-file-earmark-pdf me-2"></i> Ver mi Inscripción (PDF)
                                        </button>
                                        <button
                                            className="btn btn-success btn-lg rounded-pill fw-bold py-3 shadow-sm"
                                            onClick={resetForm}
                                        >
                                            <i className="bi bi-plus-circle me-2"></i> Registrar otra inscripción
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    ) : (
                        <div className="preview-container">
                            <div className="d-flex justify-content-between align-items-center mb-3 bg-white p-3 rounded-4 shadow-sm">
                                <h4 className="mb-0 fw-bold text-club">Vista Previa de Inscripción</h4>
                                <button className="btn btn-dark rounded-pill px-4" onClick={() => setShowPreview(false)}>
                                    <i className="bi bi-arrow-left me-2"></i> Volver
                                </button>
                            </div>
                            <div className="bg-white rounded-4 shadow-lg p-2" style={{ overflowX: 'auto' }}>
                                <Formulario initialData={formData} readOnly={true} />
                            </div>
                            <div className="text-center mt-4">
                                {editMode ? (
                                    <button className="btn btn-success btn-lg rounded-pill px-5 fw-bold" onClick={() => navigate('/dashboard')}>
                                        Volver al Panel
                                    </button>
                                ) : (
                                    <button className="btn btn-success btn-lg rounded-pill px-5 fw-bold" onClick={resetForm}>
                                        Finalizar y Salir
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    const TitulosPilotos = ['Primer Piloto', 'Segundo Piloto (Opcional)', 'Tercer Piloto (Opcional)', 'Cuarto Piloto (Opcional)'];

    if (editMode && (editLoading || !activeEvento)) {
        return (
            <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
                <div className="text-center">
                    <div className="spinner-border text-success" style={{ width: '3rem', height: '3rem' }} role="status"></div>
                    <p className="mt-3 text-muted fw-bold">Cargando inscripción para editar...</p>
                </div>
            </div>
        );
    }

    if (!editMode && eventoLoading) {
        return (
            <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
                <div className="text-center">
                    <div className="spinner-border text-success" style={{ width: '3rem', height: '3rem' }} role="status"></div>
                    <p className="mt-3 text-muted fw-bold">Cargando evento activo...</p>
                </div>
            </div>
        );
    }

    if (!editMode && !activeEvento) {
        return <LandingCerrada />;
    }

    return (
        <div className="mobile-form-wrapper bg-light pb-5">
            <div className="mobile-header text-center py-4 mb-4 shadow-sm">
                <div className="container">
                    <img
                        src="/img/logo-tortugas.png"
                        alt="Club Los Tortugas"
                        style={{ maxWidth: '180px' }}
                        className="mb-3"
                    />
                    <h2 className="fw-bold mb-1 text-white">{editMode ? `Editar Inscripción #${formId}` : 'Inscripción CNA 2026'}</h2>
                    <p className="mb-0 text-white-50">{editMode ? 'Panel de Administración' : 'Formulario Oficial de Registro'}</p>
                    <div className="mt-2 d-inline-block rounded-pill px-4 py-1" style={{ background: '#000', border: '1px solid rgba(255,255,255,0.15)' }}>
                        <span className="text-white fw-bold">
                            <i className="bi bi-calendar-check me-2"></i>{activeEvento.valida}
                        </span>
                    </div>
                </div>
            </div>

            <div className="container" style={{ maxWidth: '800px' }}>
                <form onSubmit={handlePreSubmit} noValidate>
                    {validationErrors.length > 0 && !showValidationModal && (
                        <div className="alert alert-warning border-0 rounded-4 shadow-sm mb-4 animate__animated animate__fadeIn">
                            <div className="d-flex align-items-start">
                                <i className="bi bi-exclamation-circle-fill fs-3 me-3 text-warning"></i>
                                <div>
                                    <h6 className="fw-bold mb-2">CAMPOS FALTANTES O INCORRECTOS</h6>
                                    <p className="mb-2 small text-dark">Por favor, completa los siguientes datos para poder previsualizar tu inscripción:</p>
                                    <ul className="mb-0 small ps-3">
                                        {validationErrors.map((err, i) => (
                                            <li key={i} className="mb-1">{err}</li>
                                        ))}
                                    </ul>
                                </div>
                                <button type="button" className="btn-close ms-auto" onClick={() => setValidationErrors([])}></button>
                            </div>
                        </div>
                    )}

                    {error && (
                        <div className="alert alert-danger border-0 rounded-4 shadow-sm mb-4 animate__animated animate__shakeX">
                            <div className="d-flex align-items-center">
                                <i className="bi bi-exclamation-triangle-fill fs-3 me-3"></i>
                                <div>
                                    <h6 className="fw-bold mb-1">HAY UN PROBLEMA CON TU ENVÍO</h6>
                                    <p className="mb-0 small">{error}</p>
                                </div>
                                <button type="button" className="btn-close ms-auto" onClick={() => setError(null)} aria-label="Close"></button>
                            </div>
                        </div>
                    )}

                    {/* SECCIÓN 1: DATOS DEL EVENTO */}
                    <div className="card shadow-sm border-0 rounded-4 mb-4" style={{ overflow: 'hidden' }}>
                        <div
                            className={`card-header bg-white ${openSection === 'sec1' ? 'border-bottom' : 'border-bottom-0'} pt-4 pb-3`}
                            onClick={() => toggleAccordion('sec1')}
                            style={{ cursor: 'pointer' }}
                        >
                            <div className="d-flex justify-content-between align-items-center">
                                <h4 className="fw-bold text-club mb-0"><i className="bi bi-calendar-event me-2"></i>1. Datos del Evento</h4>
                                <i className={`bi bi-chevron-${openSection === 'sec1' ? 'up' : 'down'} text-club fs-4`}></i>
                            </div>
                        </div>
                        <div className={`accordion-content ${openSection === 'sec1' ? 'open' : ''}`}>
                            <div className="card-body px-4 py-4 bg-white">
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label className="form-label text-muted small fw-bold">VÁLIDA</label>
                                        <input type="text" className="form-control form-control-lg bg-light border-0 text-muted" value={formData.valida} readOnly disabled />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label text-muted small fw-bold">FECHA</label>
                                        <input type="text" className="form-control form-control-lg bg-light border-0 text-muted" value={formData.fecha} readOnly disabled />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label text-muted small fw-bold">CIUDAD <span className="text-danger">*</span></label>
                                        <input type="text" className="form-control form-control-lg bg-light border-0" name="ciudad" value={formData.ciudad} onChange={handleChange} />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label text-muted small fw-bold">BOX NO. <span className="text-danger">*</span></label>
                                        <input type="text" inputMode="numeric" className="form-control form-control-lg bg-light border-0" name="box_no" value={formData.box_no} onChange={handleChange} />
                                    </div>
                                    <div className="col-12">
                                        <label className="form-label text-muted small fw-bold">PREPARADO POR <span className="text-danger">*</span></label>
                                        <input type="text" className="form-control form-control-lg bg-light border-0" name="preparado_por" value={formData.preparado_por} onChange={handleChange} placeholder="Nombre del preparador" />
                                    </div>
                                </div>
                                <div className="text-end mt-4">
                                    <button type="button" className="btn btn-outline-primary px-4 rounded-pill fw-bold" onClick={(e) => { e.stopPropagation(); toggleAccordion('sec2'); }}>Siguiente: Vehículo <i className="bi bi-arrow-right"></i></button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* SECCIÓN 2: INFORMACIÓN DEL VEHÍCULO */}
                    <div className="card shadow-sm border-0 rounded-4 mb-4" style={{ overflow: 'hidden' }}>
                        <div
                            className={`card-header bg-white ${openSection === 'sec2' ? 'border-bottom' : 'border-bottom-0'} pt-4 pb-3`}
                            onClick={() => toggleAccordion('sec2')}
                            style={{ cursor: 'pointer' }}
                        >
                            <div className="d-flex justify-content-between align-items-center">
                                <h4 className="fw-bold text-club mb-0"><i className="bi bi-car-front me-2"></i>2. Información del Vehículo</h4>
                                <i className={`bi bi-chevron-${openSection === 'sec2' ? 'up' : 'down'} text-club fs-4`}></i>
                            </div>
                        </div>
                        <div className={`accordion-content ${openSection === 'sec2' ? 'open' : ''}`}>
                            <div className="card-body px-4 py-4 bg-white">
                                <div className="row g-3">
                                    <div className="col-md-5">
                                        <label className="form-label text-muted small fw-bold">NOMBRE DEL EQUIPO <span className="text-danger">*</span></label>
                                        <input type="text" className="form-control form-control-lg bg-light border-0" name="nombre_equipo" value={formData.nombre_equipo} onChange={handleChange} />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label text-muted small fw-bold">CATEGORÍA <span className="text-danger">*</span></label>
                                        <select className="form-select form-select-lg bg-light border-0" name="categoria" value={formData.categoria} onChange={handleChange}>
                                            <option value="">Seleccione una categoría</option>
                                            <option value="FUERZA LIBRE FL">FUERZA LIBRE FL</option>
                                            <option value="PROTOTIPO P1">PROTOTIPO P1</option>
                                            <option value="GRAN TURISMO GT">GRAN TURISMO GT</option>
                                            <option value="TURBO TURISMO TT">TURBO TURISMO TT</option>
                                            <option value="SUPER TURISMO ST1">SUPER TURISMO ST1</option>
                                            <option value="SUPER TURISMO ST2">SUPER TURISMO ST2</option>
                                            <option value="T2">T2</option>
                                            <option value="T2B">T2B</option>
                                        </select>
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label text-muted small fw-bold">No. VEHÍCULO <span className="text-danger">*</span></label>
                                        <input type="text" inputMode="numeric" className={`form-control form-control-lg bg-light border-0 ${editMode ? 'text-muted' : ''}`} name="no_vehiculo" value={formData.no_vehiculo} onChange={handleChange} placeholder="Ej. 123" readOnly={editMode} />
                                    </div>
                                    <div className="col-12">
                                        <label className="form-label text-muted small fw-bold">PATROCINADORES <span className="text-danger">*</span></label>
                                        <input type="text" className="form-control form-control-lg bg-light border-0" name="patrocinadores" value={formData.patrocinadores} onChange={handleChange} />
                                    </div>
                                    <div className="col-12">
                                        <label className="form-label text-muted small fw-bold">DELEGADO <span className="text-danger">*</span></label>
                                        <input type="text" className="form-control form-control-lg bg-light border-0" name="delegado" value={formData.delegado} onChange={handleChange} />
                                    </div>

                                    <h6 className="mt-4 mb-2 text-secondary fw-bold border-bottom pb-2">Especificaciones Técnicas</h6>
                                    <div className="col-6 col-md-4">
                                        <label className="form-label text-muted small fw-bold">MARCA <span className="text-danger">*</span></label>
                                        <input type="text" className="form-control bg-light border-0" name="marca_vehiculo" value={formData.marca_vehiculo} onChange={handleChange} />
                                    </div>
                                    <div className="col-6 col-md-4">
                                        <label className="form-label text-muted small fw-bold">MOTOR <span className="text-danger">*</span></label>
                                        <input type="text" className="form-control bg-light border-0" name="motor" value={formData.motor} onChange={handleChange} />
                                    </div>
                                    <div className="col-6 col-md-4">
                                        <label className="form-label text-muted small fw-bold">PESO MÍNIMO <span className="text-danger">*</span></label>
                                        <input type="text" inputMode="numeric" className="form-control bg-light border-0" name="peso_minimo" value={formData.peso_minimo} onChange={handleChange} />
                                    </div>
                                    <div className="col-6 col-md-4">
                                        <label className="form-label text-muted small fw-bold">CILINDRAJE <span className="text-danger">*</span></label>
                                        <input type="text" inputMode="numeric" className="form-control bg-light border-0" name="cilindrad" value={formData.cilindrad} onChange={handleChange} />
                                    </div>
                                    <div className="col-6 col-md-4">
                                        <label className="form-label text-muted small fw-bold">TIPO <span className="text-danger">*</span></label>
                                        <input type="text" className="form-control bg-light border-0" name="tipo" value={formData.tipo} onChange={handleChange} />
                                    </div>
                                    <div className="col-6 col-md-4">
                                        <label className="form-label text-muted small fw-bold">COLOR <span className="text-danger">*</span></label>
                                        <input type="text" className="form-control bg-light border-0" name="color" value={formData.color} onChange={handleChange} />
                                    </div>
                                </div>
                                <div className="text-end mt-4">
                                    <button type="button" className="btn btn-outline-primary px-4 rounded-pill fw-bold" onClick={(e) => { e.stopPropagation(); toggleAccordion('piloto0'); }}>Siguiente: Pilotos <i className="bi bi-arrow-right"></i></button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* SECCIÓN 3: PILOTOS */}
                    {[...Array(pilotosActivos)].map((_, index) => {
                        const piloto = formData.datos_pilotos[index] || INITIAL_PILOTO;
                        const sectionKey = `piloto${index}`;
                        const isMandatory = index === 0;
                        return (
                            <div className="card shadow-sm border-0 rounded-4 mb-4 border-start border-club border-4" key={index} style={{ overflow: 'hidden' }}>
                                <div
                                    className={`card-header bg-white ${openSection === sectionKey ? 'border-bottom' : 'border-bottom-0'} pt-4 pb-3`}
                                    onClick={() => toggleAccordion(sectionKey)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <div className="d-flex justify-content-between align-items-center">
                                        <h4 className="fw-bold text-dark mb-0">
                                            <i className="bi bi-person-badge me-2 text-club"></i>
                                            {TitulosPilotos[index]}
                                            {isMandatory && <span className="text-danger ms-1">*</span>}
                                        </h4>
                                        <div className="d-flex align-items-center">
                                            {index > 0 && (
                                                <button type="button" className="btn btn-sm btn-outline-danger rounded-pill px-3 me-3" onClick={(e) => { e.stopPropagation(); setPilotosActivos(prev => prev - 1); }}>
                                                    Quitar
                                                </button>
                                            )}
                                            <i className={`bi bi-chevron-${openSection === sectionKey ? 'up' : 'down'} text-club fs-4`}></i>
                                        </div>
                                    </div>
                                </div>
                                <div className={`accordion-content ${openSection === sectionKey ? 'open' : ''}`}>
                                    <div className="card-body px-4 py-4 bg-white">
                                        <div className="row g-3">
                                            <div className="col-md-8">
                                                <label className="form-label text-muted small fw-bold">NOMBRES Y APELLIDOS <span className="text-danger">*</span></label>
                                                <input type="text" className="form-control bg-light border-0" value={piloto.nombres} onChange={(e) => handlePilotoChange(index, 'nombres', e.target.value)} />
                                            </div>
                                            <div className="col-md-4">
                                                <label className="form-label text-muted small fw-bold">CÉDULA (C.C) <span className="text-danger">*</span></label>
                                                <input type="text" inputMode="numeric" className="form-control bg-light border-0" value={piloto.cc} onChange={(e) => handlePilotoChange(index, 'cc', e.target.value)} />
                                            </div>
                                            <div className="col-md-4">
                                                <label className="form-label text-muted small fw-bold">Lic F.C.A.D. No. <span className="text-danger">*</span></label>
                                                <input type="text" className="form-control bg-light border-0" value={piloto.lic_fcad} onChange={(e) => handlePilotoChange(index, 'lic_fcad', e.target.value)} />
                                            </div>
                                            <div className="col-md-4">
                                                <label className="form-label text-muted small fw-bold">CLUB <span className="text-danger">*</span></label>
                                                <input type="text" className="form-control bg-light border-0" value={piloto.club} onChange={(e) => handlePilotoChange(index, 'club', e.target.value)} />
                                            </div>
                                            <div className="col-md-4">
                                                <label className="form-label text-muted small fw-bold">CELULAR <span className="text-danger">*</span></label>
                                                <input type="tel" inputMode="tel" className="form-control bg-light border-0" value={piloto.celular} onChange={(e) => handlePilotoChange(index, 'celular', e.target.value)} />
                                            </div>
                                            <div className="col-md-4">
                                                <label className="form-label text-muted small fw-bold">E.P.S. <span className="text-danger">*</span></label>
                                                <input type="text" className="form-control bg-light border-0" value={piloto.eps} onChange={(e) => handlePilotoChange(index, 'eps', e.target.value)} />
                                            </div>
                                            <div className="col-md-4">
                                                <label className="form-label text-muted small fw-bold">RH (SANGRE) <span className="text-danger">*</span></label>
                                                <input type="text" className="form-control bg-light border-0" value={piloto.rh} onChange={(e) => handlePilotoChange(index, 'rh', e.target.value)} placeholder="Ej. O+" />
                                            </div>
                                            <div className="col-md-8">
                                                <label className="form-label text-muted small fw-bold">E-MAIL <span className="text-danger">*</span></label>
                                                <input type="email" className="form-control bg-light border-0" value={piloto.email} onChange={(e) => handlePilotoChange(index, 'email', e.target.value)} />
                                            </div>
                                            <div className="col-md-4">
                                                <label className="form-label text-muted small fw-bold">CIUDAD <span className="text-danger">*</span></label>
                                                <input type="text" className="form-control bg-light border-0" value={piloto.ciudad} onChange={(e) => handlePilotoChange(index, 'ciudad', e.target.value)} />
                                            </div>
                                            <div className="col-md-4">
                                                <label className="form-label text-muted small fw-bold">TEL. OFICINA <span className="text-danger">*</span></label>
                                                <input type="text" inputMode="numeric" className="form-control bg-light border-0" value={piloto.tel_of} onChange={(e) => handlePilotoChange(index, 'tel_of', e.target.value)} />
                                            </div>
                                            <div className="col-md-8">
                                                <label className="form-label text-muted small fw-bold">DIRECCIÓN <span className="text-danger">*</span></label>
                                                <input type="text" className="form-control bg-light border-0" value={piloto.direccion} onChange={(e) => handlePilotoChange(index, 'direccion', e.target.value)} />
                                            </div>

                                            <div className="col-12 mt-3">
                                                <label className="form-label text-muted small fw-bold d-block mb-2">
                                                    FIRMA DEL PILOTO {index + 1} <span className="text-danger">*</span>
                                                </label>
                                                <div className="signature-pad-container bg-light rounded-3 p-2 border" style={{ height: '200px', cursor: 'crosshair' }}>
                                                    <SignatureCanvas
                                                        ref={signaturePads[index]}
                                                        penColor="black"
                                                        onEnd={() => handleSignatureEnd(index)}
                                                        canvasProps={{
                                                            className: 'signature-canvas w-100 h-100',
                                                            style: { minHeight: '180px' }
                                                        }}
                                                    />
                                                </div>
                                                <div className="text-start mt-2">
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-secondary rounded-pill px-3"
                                                        onClick={(e) => { e.stopPropagation(); signaturePads[index].current.clear(); }}
                                                    >
                                                        <i className="bi bi-eraser me-1"></i> Borrar Firma
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="col-12 mt-4 pt-3 border-top">
                                                <label className="form-label text-muted small fw-bold d-block mb-2">
                                                    <i className="bi bi-shield-check me-2 text-primary"></i>CERTIFICADO DE ARL (Opcional)
                                                </label>
                                                <div className="d-flex align-items-center flex-wrap gap-2">
                                                    <input
                                                        type="file"
                                                        className="form-control bg-light border-0"
                                                        style={{ flex: '1 1 200px' }}
                                                        accept=".pdf,image/png,image/jpeg,image/webp"
                                                        onChange={(e) => handleFileChange(index, e)}
                                                        ref={fileInputRefs[index]}
                                                    />

                                                    {formData.datos_pilotos[index].arl_filename && (
                                                        <div className="d-flex align-items-center bg-light p-2 rounded-3 border border-primary border-opacity-25" style={{ flex: '0 0 auto', maxWidth: '100%' }}>
                                                            <div className="text-truncate fw-bold text-primary small me-2" style={{ maxWidth: '250px' }}>
                                                                <i className="bi bi-file-earmark-check-fill me-2"></i>
                                                                {formData.datos_pilotos[index].arl_filename}
                                                            </div>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-danger px-2 rounded-2 shadow-sm"
                                                                onClick={(e) => { e.stopPropagation(); clearFile(index); }}
                                                                title="Eliminar archivo"
                                                            >
                                                                <i className="bi bi-trash"></i>
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                                <small className="form-text text-muted" style={{ fontSize: '0.7rem' }}>Sube una imagen o PDF con tu afiliación vigente a la ARL. Max 5MB. Puedes reemplazar el archivo haciendo clic de nuevo.</small>
                                            </div>
                                        </div>

                                        <div className="text-end mt-4">
                                            {index === pilotosActivos - 1 ? (
                                                <button type="button" className="btn btn-outline-success px-4 rounded-pill fw-bold" onClick={(e) => { e.stopPropagation(); toggleAccordion('terms'); }}>Ir a Finalizar <i className="bi bi-check2-circle"></i></button>
                                            ) : (
                                                <button type="button" className="btn btn-outline-primary px-4 rounded-pill fw-bold" onClick={(e) => { e.stopPropagation(); toggleAccordion(`piloto${index + 1}`); }}>Siguiente Piloto <i className="bi bi-arrow-right"></i></button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                    {pilotosActivos < 4 && (
                        <div className="text-center mb-5">
                            <button
                                type="button"
                                className="btn btn-outline-primary rounded-pill px-4 py-2 border-2 fw-bold bg-white"
                                onClick={() => {
                                    const nextCount = pilotosActivos + 1;
                                    setFormData(prev => {
                                        const nuevosPilotos = [...prev.datos_pilotos];
                                        while (nuevosPilotos.length < nextCount) {
                                            nuevosPilotos.push({ ...INITIAL_PILOTO });
                                        }
                                        return { ...prev, datos_pilotos: nuevosPilotos };
                                    });
                                    setPilotosActivos(nextCount);
                                    setOpenSection(`piloto${pilotosActivos}`);
                                }}
                            >
                                <i className="bi bi-plus-circle me-2"></i> Añadir Otro Piloto
                            </button>
                        </div>
                    )}

                    {/* SECCIÓN 4: TERMINOS Y CONDICIONES */}
                    <div className="card shadow-sm border-0 rounded-4 mb-4 bg-light bg-opacity-50" style={{ overflow: 'hidden' }}>
                        <div
                            className={`card-header ${openSection === 'terms' ? 'bg-warning bg-opacity-10 border-bottom' : 'bg-white border-bottom-0'} pt-4 pb-3`}
                            onClick={() => toggleAccordion('terms')}
                            style={{ cursor: 'pointer' }}
                        >
                            <div className="d-flex justify-content-between align-items-center">
                                <h4 className="fw-bold text-dark mb-0"><i className="bi bi-file-earmark-text me-2"></i>Términos y Condiciones</h4>
                                <i className={`bi bi-chevron-${openSection === 'terms' ? 'up' : 'down'} text-dark fs-4`}></i>
                            </div>
                        </div>
                        <div className={`accordion-content ${openSection === 'terms' ? 'open' : ''}`}>
                            <div className="card-body px-4 py-4">
                                <div className="alert alert-warning border-0 bg-warning bg-opacity-10 text-dark small text-justify rounded-3">
                                    <p className="mb-0">
                                        <strong>Declaración de responsabilidad:</strong> Las personas abajofirmantes, certificamos que conocemos en todo su contenido, el Reglamento Deportivo Nacional (RDN) 2026, el Reglamento General del Campeonato, el Reglamento Particular; y por ende, declaramos que los datos que aquí consignamos, entre ellos el estado actual de nuestro vehículo, son verdaderos y se ajustan a la reglamentación vigente. Igualmente, aceptamos que la Federación Colombiana de Automovilismo Deportivo, Autódromos S.A., los clubes afiliados y los organizadores <strong>NO ASUMEN</strong> ninguna responsabilidad por daños causados a nuestra integridad física, ni a terceros, durante nuestra participación en esta competencia. Además autorizamos la utilización de nuestros nombres, fotografías, resultados, testimonios y comentarios, para ser difundidas por el club organizador y/o sus patrocinadores.
                                    </p>
                                </div>

                                <div className="form-check mt-3 ps-4">
                                    <input
                                        className="form-check-input form-check-input-lg border-2 border-club"
                                        type="checkbox"
                                        id="terminosCheck"
                                        checked={terminosAceptados}
                                        onChange={(e) => setTerminosAceptados(e.target.checked)}
                                    />
                                    <label className="form-check-label fw-bold ms-2 mt-1" htmlFor="terminosCheck" style={{ cursor: 'pointer' }}>
                                        He leído y acepto los términos, condiciones y la exención de responsabilidad descrita anteriormente.
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="d-grid gap-2 mb-5">
                        <button
                            type="submit"
                            className="btn btn-primary btn-lg rounded-pill py-3 fw-bold shadow-lg"
                            disabled={loading || !terminosAceptados}
                        >
                            <i className="bi bi-eye me-2"></i> PREVISUALIZAR INSCRIPCIÓN
                        </button>
                    </div>
                </form>
            </div>

            {showValidationModal && (
                <div className="modal-overlay d-flex align-items-center justify-content-center p-3" style={{
                    position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
                    backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 9999, backdropFilter: 'blur(5px)'
                }}>
                    <div className="card border-0 rounded-4 shadow-lg animate__animated animate__zoomIn" style={{ maxWidth: '450px', width: '100%' }}>
                        <div className="card-header bg-warning py-3 border-0 text-center">
                            <i className="bi bi-exclamation-triangle-fill fs-1 text-dark mb-2"></i>
                            <h5 className="fw-bold mb-0 text-dark">DATOS FALTANTES</h5>
                        </div>
                        <div className="card-body p-4">
                            <p className="text-muted small mb-3">No podemos procesar tu previsualización porque faltan los siguientes campos obligatorios:</p>
                            <div className="validation-scroll-area pe-2" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                                <ul className="list-group list-group-flush small">
                                    {validationErrors.map((err, i) => (
                                        <li key={i} className="list-group-item px-0 border-light d-flex align-items-start">
                                            <i className="bi bi-x-circle-fill text-danger me-2 mt-1"></i>
                                            <span className="fw-bold text-dark">{err}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="d-grid mt-4">
                                <button className="btn btn-dark btn-lg rounded-pill fw-bold" onClick={() => setShowValidationModal(false)}>
                                    ENTENDIDO, VOY A CORREGIR
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FormularioMobile;
